/**
 * Login backdrop (premium-01 §16, Phase D). A layered map-motif with depth:
 *
 *  1. a **base gradient** field (soft teal glow, top-right) instead of a flat
 *     fill;
 *  2. a **back room-grid** layer — larger, offset rooms at ~0.06 opacity that
 *     drift very slowly for parallax depth (`.motif-back`);
 *  3. the **front motif** — dotted grid + room footprints + an animated teal
 *     route polyline (draw-in, delayed to land after the card) whose halo gently
 *     breathes, with a two-ring radial pulse on the origin dot;
 *  4. a **soft vignette** + the original white→transparent bottom fade.
 *
 * Calm by design (it sits behind the login card) and decorative only
 * (`aria-hidden`). Every animation is frozen under the reduced-motion reset.
 */
export function MapMotifBackground() {
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{
        background:
          "radial-gradient(120% 90% at 80% 10%, rgba(23,174,191,0.10) 0%, transparent 46%), " +
          "linear-gradient(165deg, #F7FAFA 0%, #F1F6F6 52%, #E9F1F1 100%)",
      }}
      aria-hidden
    >
      {/* ── Back depth layer: larger, offset rooms, slow parallax drift. ── */}
      <div className="motif-layer motif-back">
        <svg
          className="h-full w-full"
          viewBox="0 0 1440 900"
          preserveAspectRatio="xMidYMid slice"
          style={{ opacity: 0.06 }}
        >
          <g fill="none" stroke="#0E8A99" strokeWidth="2.5">
            <rect x="150" y="120" width="290" height="210" rx="12" />
            <rect x="150" y="360" width="290" height="210" rx="12" />
            <rect x="470" y="110" width="360" height="270" rx="12" />
            <rect x="470" y="430" width="360" height="210" rx="12" />
            <rect x="1080" y="140" width="310" height="500" rx="12" />
            <rect x="470" y="700" width="720" height="150" rx="12" />
          </g>
        </svg>
      </div>

      {/* ── Front motif: dots + rooms + route + markers. ── */}
      <div className="motif-layer">
        <svg
          className="h-full w-full"
          viewBox="0 0 1440 900"
          preserveAspectRatio="xMidYMid slice"
          style={{ opacity: 0.55 }}
        >
          <defs>
            <pattern id="login-dots" width="32" height="32" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="2" fill="#17AEBF" opacity="0.12" />
            </pattern>
          </defs>

          {/* dotted grid */}
          <rect x="0" y="0" width="1440" height="900" fill="url(#login-dots)" />

          {/* room footprints */}
          <g fill="none" stroke="#17AEBF" strokeWidth="1.5" opacity="0.22">
            <rect x="120" y="140" width="240" height="170" rx="10" />
            <rect x="120" y="360" width="240" height="170" rx="10" />
            <rect x="430" y="140" width="300" height="220" rx="10" />
            <rect x="430" y="430" width="300" height="170" rx="10" />
            <rect x="1040" y="160" width="260" height="420" rx="10" />
            <rect x="430" y="660" width="600" height="120" rx="10" />
          </g>

          {/* route polyline (draw-in, delayed) + gently breathing halo */}
          <polyline
            className="motif-breathe"
            points="240,470 240,250 580,250 580,470 1170,470 1170,300"
            fill="none"
            stroke="#17AEBF"
            strokeWidth="18"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            className="route-draw"
            points="240,470 240,250 580,250 580,470 1170,470 1170,300"
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ strokeDasharray: 2000, strokeDashoffset: 2000, animationDelay: "300ms" }}
          />

          {/* destination marker */}
          <circle cx="1170" cy="300" r="13" fill="#FFFFFF" stroke="#0E8A99" strokeWidth="3" />
          <circle cx="1170" cy="300" r="5" fill="#0E8A99" />

          {/* origin dot — two staggered radial pulse rings + solid pin */}
          <circle cx="240" cy="470" r="9" fill="#17AEBF" opacity="0.55" className="origin-ring" />
          <circle
            cx="240"
            cy="470"
            r="9"
            fill="#17AEBF"
            opacity="0.55"
            className="origin-ring origin-ring-2"
          />
          <circle cx="240" cy="470" r="9" fill="#17AEBF" stroke="#FFFFFF" strokeWidth="3" />
        </svg>
      </div>

      {/* soft vignette — darkens the far edges a hair for depth */}
      <div
        className="motif-layer"
        style={{
          background:
            "radial-gradient(120% 100% at 50% 42%, transparent 55%, rgba(17,24,28,0.05) 100%)",
        }}
      />

      {/* white→transparent bottom fade */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/2"
        style={{
          background: "linear-gradient(to top, #FFFFFF 0%, rgba(255,255,255,0) 100%)",
        }}
      />
    </div>
  );
}
